import React from 'react';

export type FileTemplateKey = 'component' | 'hook' | 'api' | 'sandbox' | 'runner' | 'empty';

export function getLanguage(path: string) {
  if (path.endsWith('.ts') || path.endsWith('.tsx')) return 'typescript';
  if (path.endsWith('.js') || path.endsWith('.jsx')) return 'javascript';
  if (path.endsWith('.json')) return 'json';
  if (path.endsWith('.css')) return 'css';
  return 'plaintext';
}

export function toPascalCase(name: string) {
  return name
    .replace(/\.[^/.]+$/, '')
    .split(/[^a-zA-Z0-9]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('') || 'NewItem';
}

export function getFileTemplate(template: FileTemplateKey, filename: string): string {
  const name = toPascalCase(filename);
  switch (template) {
    case 'component':
      return `import React from 'react';

type Props = {
  children?: React.ReactNode;
};

function ${name}(props: Props) {
  return <div className="${name.toLowerCase()}">{props.children || 'Hello ${name}'}</div>;
}

export default ${name};
`;
    case 'hook':
      return `import { useEffect, useState } from 'react';

export function use${name}(initialValue = null) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    // side effects here
  }, []);

  return { value, setValue };
}
`;
    case 'api':
      return `const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.example.com';

export async function fetch${name}(path: string, options: RequestInit = {}) {
  const res = await fetch(\`\${API_BASE}/\${path}\`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) throw new Error(\`API error \${res.status}\`);
  return res.json();
}
`;
    case 'sandbox':
      return `export function Sandbox${name}() {
  const message = 'Sandbox example';
  return <pre>{message}</pre>;
}

export default Sandbox${name};
`;
    case 'runner':
      return `type TaskResult = {
  task: string;
  status: 'ok' | 'error';
  output?: string;
};

export async function run${name}(task: string): Promise<TaskResult> {
  try {
    // simulate runner
    return { task, status: 'ok', output: 'Runner completed' };
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    return { task, status: 'error', output: msg };
  }
}
`;
    default:
      return '';
  }
}

export function getTemplateDefaultName(template: FileTemplateKey) {
  switch (template) {
    case 'component':
      return 'NewComponent.tsx';
    case 'hook':
      return 'useNewHook.ts';
    case 'api':
      return 'apiClient.ts';
    case 'sandbox':
      return 'SandboxExample.tsx';
    case 'runner':
      return 'runner.ts';
    case 'empty':
    default:
      return 'NewFile.tsx';
  }
}

export function ensureDefaultExport(src: string) {
  const hasDefaultExport = /export\s+default\s+/m.test(src);
  if (hasDefaultExport) return src;

  const appNameMatch = src.match(/function\s+([A-Z][A-Za-z0-9_]*)\s*\(/);
  const appName = appNameMatch?.[1] || 'App';
  return `${src}\nexport default ${appName};`;
}

export function scheduleLocalStorageSave(
  timers: React.MutableRefObject<Record<string, number>>,
  key: string,
  value: unknown,
  onError?: (error: unknown) => void
) {
  if (timers.current[key]) {
    window.clearTimeout(timers.current[key]);
  }
  timers.current[key] = window.setTimeout(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('localStorage save error', error);
      if (onError) onError(error);
    }
  }, 400);
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function safeParse<T>(json: string | null): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}
